import "react-datepicker/dist/react-datepicker.css";
import { useEffect, useMemo, useState } from "react";
import { ReportApi } from "src/services/api/ReportApi";
import {
  ColDef,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  LoadSuccessParams,
  GridApi,
  ColGroupDef,
  ValueFormatterParams,
  SizeColumnsToContentStrategy,
} from "ag-grid-enterprise";
import useSidebar from "src/hooks/useSidebar";
import BaseGrid from "../../BaseGrid";
import { downloadFile } from "src/utils/DownloadFile";
import useReport from "src/hooks/useReport";
import LoadingComponent from "src/components/LoadingComponent";
import SearchCriteriaRedReports from "src/components/SearchCriteriaRedReports";
import { formatCurrency } from "src/utils/FormatCurrency";
import {
  AgBarSeriesOptions,
  AgChartOptions,
} from "ag-charts-enterprise";
import { DiscrepancyAmountByDate } from "src/models/DiscrepancyAmountByDate";
import useTheme from "src/hooks/useTheme";
import { AgCharts } from "ag-charts-react";
import { VisaSettlementReconciliationModel } from "src/models/VisaSettlementReconciliationModel";
import { VisaSettlementReconciliationSummaryModel } from "src/models/VisaSettlementReconciliationSummaryModel";

const VisaSettlementReconciliation = () => {
  const { setIsOpen } = useSidebar();
  const { theme } = useTheme();
  const { reportInputs, mapReportRequest, getContextMenuItems } = useReport();

  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [gridReady, setGridReady] = useState(false);
  const [dataSource, setDataSource] = useState<IServerSideDatasource>();
  const [gridApi, setGridApi] = useState<GridApi>();
  const [isDownload, setIsDownload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const nextOptions = { ...options };
    nextOptions.theme = theme === "dark" ? "ag-material-dark" : "ag-material";
    setOptions(nextOptions);
  }, [theme]);

  const handleViewReport = () => {
    setIsDownload(false);
    setGridReady(true);
    setDataSource(createServerSideDatasource());
  };

  const handleDownloadReport = () => {
    setIsDownload(true);
    setIsLoading(true);
    ReportApi.exportVisaSettlementReconciliation(mapReportRequest(null))
      .then((res) => {
        downloadFile(VisaSettlementReconciliation.displayName, res.data);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const currencyFormatter = (
    params: ValueFormatterParams<VisaSettlementReconciliationModel, any>
  ) => {
    if (params.value == undefined || params.data == undefined) return "";
    if (params.value === 0) return "-";
    const alphaCode = params.data?.alphaCode;
    const cur = formatCurrency(params.value, alphaCode);
    if (params.value < 0) {
      const formattedCur = cur + "";
      return "(" + formattedCur.replace("-", "") + ")";
    }
    return cur + "";
  };

  const [colDefs] = useState<
    | ColDef<VisaSettlementReconciliationModel>[]
    | ColGroupDef<VisaSettlementReconciliationModel>[]
  >([
    {
      headerName: "Files Processed",

      children: [
        {
          headerName: "",
          children: [
            {
              headerName: "Currency Code",
              field: "currencyCode",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              minWidth: 80,
            },
            {
              headerName: "Clearing Date",
              field: "clearingDate",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              minWidth: 80,
            },            
            {
              headerName: "Clearing Cycle",
              field: "clearingCycle",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              minWidth: 100,
            },
            {
              headerName: "Bin",
              field: "bin",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              minWidth: 120,
            },
          ],
        },
      ],
    },
    {
      headerName: "Visa Data",
      headerStyle: { textAlign: "center" },
      children: [
        {
          headerName: "Reconciliation",
          children: [
            {
              headerName: "Amount Net Total",
              field: "reconciliationAmountNetTotal",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 120,
            },            
          ],
        },
        {
          headerName: "Presentments",
          children: [
            {
              headerName: "Count",
              field: "visaPresentmentCount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              minWidth: 100,
            },
            {
              headerName: "Amount",
              field: "visaPresentmentAmount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
          ],
        },
        {
          headerName: "Non-Cardholder",
          wrapHeaderText: true,
          autoHeaderHeight: true,
          children: [
            {
              headerName: "Amount",
              field: "visaNonCardHolderIncome",
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
            {
              headerName: "Fees",
              field: "visaNonCardHolderFees",
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
          ],
        },
        {
          headerName: "",
          children: [
            {
              headerName: "Total Settlement",
              field: "visaTotalSettlement",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 120,
            },
          ],
        },
      ],
    },
    {
      headerName: "EML System Data",
      children: [
        {
          headerName: "Presentments",
          children: [
            {
              headerName: "Count",
              field: "emlPresentmentCount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              minWidth: 100,
            },
            {
              headerName: "Client Programs",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              field: "emlPresentmentClientProgramsAmount",
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
            {
              headerName: "Other Non-Cardholder Settlement Amount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              field: "emlPresentmentNonCardHolderAmount",
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 120,
            },
            {
              headerName: "Internal Programs",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              field: "emlPresentmentInternalProgramsAmount",
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
            {
              headerName: "Balance Inquiry",
              field: "emlPresentmentBalanceInquiryAmount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
            {
              headerName: "Total Processed",
              field: "emlPresentmentTotalSettlementAmount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
          ],
        },
        {
          headerName: "",
          children: [
            {
              field: "discrepancy",
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 80,
            },
          ],
        },
      ],
    },
  ]);

  const defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    cellStyle: { textAlign: "center" },
  };

  const autoSizeStrategy = useMemo<SizeColumnsToContentStrategy>(() => {
    return {
      type: "fitCellContents",
      defaultMinWidth: 60,
      defaultMaxWidth: 150,
      skipHeader: true,
    };
  }, []);

  const createServerSideDatasource: () => IServerSideDatasource = () => {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        getReportData(params);
      },
    };
  };

  const getReportData = (params: IServerSideGetRowsParams | null) => {
    setIsOpen(false);
    ReportApi.getVisaSettlementReconciliation(mapReportRequest(params))
      .then((res) => {
        const result = res.data;
        result?.rows.map(
          (x: VisaSettlementReconciliationModel) =>
            ((x.clearingDate = x.clearingDate
              ?  new Date(x.clearingDate).toLocaleDateString() : ""),
              (x.bin = x.rawDataFileId === null ? "Totals:" : x.bin)),                            
        );
        const successParams: LoadSuccessParams = {
          rowData: result.rows,
          rowCount: result.totalCount,
        };
        if ((result?.rows?.length ?? 0) > 0) {
          params?.api?.hideOverlay();
          params?.success(successParams);
        } else {
          params?.api?.showNoRowsOverlay();
          params?.success({ rowData: [], rowCount: 0 });
        }
        const cd = mapChartData(result.summaryDetail);
        setChartData(cd);
        setOptions((prevOptions) => ({
          ...prevOptions,
          data: cd,
        }));
      })
      .catch(() => {
        params?.fail();
      });
  };

  const mapChartData = (
    data: VisaSettlementReconciliationSummaryModel
  ) => {
    const discrepancyAmountsByDate: DiscrepancyAmountByDate[] = [];
    data.discrepancyAmounts.map((ca) => {
      const discrepancyAmount: DiscrepancyAmountByDate = {
        amount: ca.amount,
        date: new Date(ca.date).toLocaleDateString(),
      };
      discrepancyAmountsByDate.push(discrepancyAmount);
    });
    return discrepancyAmountsByDate;
  };

  const [options, setOptions] = useState<AgChartOptions>({
    title: {
      text: "Discrepancy Amount By Date",
    },
    // Data: Data to be displayed in the chart
    data: chartData as ChartData[],
    // Series: Defines which chart type and data to use
    series: [
      {
        type: "bar",
        xKey: "date",
        yKey: "amount",        
      } as AgBarSeriesOptions,
    ],    
    theme: theme === "dark" ? "ag-material-dark" : "ag-material",
  });

  return (
    <>
      <div className="report-container">
        <div className="report-header">
          <h5 className="mb-0">Visa Settlement Reconciliation</h5>
          {(reportInputs?.icaValue?.length ?? 0) > 0 &&
            reportInputs?.fromDate &&
            reportInputs?.toDate && (
              <div className="report-header-date">{`${reportInputs?.fromDate.toLocaleDateString()} - ${reportInputs?.toDate.toLocaleDateString()}`}</div>
            )}
        </div>
        <div className="blocks">
          <SearchCriteriaRedReports
            handleViewReport={handleViewReport}
            handleDownloadReport={handleDownloadReport} 
            useIcaValue={false}           
            useBin={true}
          />
          {(chartData?.length ?? 0) > 0 && !isDownload && (
            <div className="report-chart">
              <AgCharts options={options} />
            </div>
          )}
        </div>
        {isDownload && isLoading ? (
          <LoadingComponent />
        ) : (
          !isDownload && (
            <BaseGrid
              colDefs={colDefs}
              defaultColDef={defaultColDef}
              getData={getReportData}
              isGridReady={gridReady}
              reportInputs={reportInputs}
              dataSource={dataSource}
              gridApi={gridApi}
              setGridApi={setGridApi}
              getContextMenuItems={getContextMenuItems}
              autoSizeStrategy={autoSizeStrategy}
            />
          )
        )}
      </div>
    </>
  );
};

VisaSettlementReconciliation.displayName =
  "VisaSettlementReconciliation";

export default VisaSettlementReconciliation;
